import { createApi } from "@reduxjs/toolkit/query/react";
import { CONFIG_VARS } from "../../constants/urlConstants/configuration";
import { createBaseQuery } from "../../shared/utils/baseQuery";

const { gatewayApiVersion, baseUrl, gatewayBaseUrl, apiVersion } = CONFIG_VARS;

export const inviteCodeApi = createApi({
  reducerPath: "inviteCodeApi",
  baseQuery: createBaseQuery(baseUrl["1"], true), // Enable auth token
  endpoints: (builder) => ({
    getInviteCodes: builder.query({
      query: (data) => ({
        url: `/${apiVersion}/activation-codes/invitation-codes?page=${
          data.page
        }&limit=${data.limit}&status=${data.status || "ALL"}&search_query=${
          data.searchQuery || ""
        }`,
        method: "POST",
      }),
      // Disable caching to ensure loading state is always tracked
      keepUnusedDataFor: 0,
      // Force refetch on any parameter change
      forceRefetch: ({ currentArg, previousArg }) => {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
      },
      transformResponse: (response) => {
        if (response.error) {
          throw new Error(`${response.error.message}`);
        }
        const transformedData = response.results.map((item) => ({
          tenantId: item.tenant_id || "",
          tenantName: item.tenant_name || "",
          invitationCode: item.invitation_code || "",
          schoolName: item.school_name || "",
          schoolAddress: item.school_address || "",
          status: item.status || "",
        }));

        return {
          inviteCodes: transformedData,
          totalCount: response.total_count,
        };
      },
      transformErrorResponse: (response) => {
        return {
          status: response.status || "400",
          message:
            response.data?.message ||
            "An error occurred while fetching invite codes",
        };
      },
    }),
  }),
});

export const tenantSearchApi = createApi({
  reducerPath: "tenantSearchApi",
  baseQuery: createBaseQuery(gatewayBaseUrl["1"], true), // Enable auth token
  endpoints: (builder) => ({
    searchTenants: builder.mutation({
      query: (data) => ({
        url: `/${gatewayApiVersion}/tenant/search?page=${data.page}&limit=${data.limit}&productId=${data.productId}`,
        method: "POST",
        body: {
          search_text: data.searchText,
          wild_search: true,
          source: "IM",
        },
      }),
      transformResponse: (response) => {
        if (!response || response.error) {
          throw new Error(
            response?.error?.message || "Failed to search tenants"
          );
        }
        return {
          tenants: response.tenants || [],
          totalCount: response.totalCount || 0,
          currentPage: response.currentPage || 1,
          limit: response.limit || 20,
        };
      },
      transformErrorResponse: (response) => {
        return {
          status: response.status || "400",
          message:
            response.data?.message ||
            "An error occurred while searching tenants",
        };
      },
    }),
  }),
});

export const activationCodesAPI = createApi({
  reducerPath: "activationCodesAPI",
  baseQuery: createBaseQuery(baseUrl["1"], true), // Enable auth token
  endpoints: (builder) => ({
    createAndMapActivationCode: builder.mutation({
      query: (data) => ({
        url: `/${apiVersion}/activation-codes/create-and-map`,
        method: "POST",
        body: data.body,
      }),
    }),
  }),
});

export const { useGetInviteCodesQuery } = inviteCodeApi;
export const { useSearchTenantsMutation } = tenantSearchApi;
export const { useCreateAndMapActivationCodeMutation } = activationCodesAPI;
